// client/src/components/unidades/UnidadesPage.js
import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import { useUnidadesPorConta, useMutateUnidades } from '../../hooks/useUnidades';
import {
    MapPin,
    PlusCircle,
    Plus,
    Loader2,
    Search,
    Filter,
    X,
    Grid3x3,
    List,
    Eye,
    Power,
    Trash2
} from 'lucide-react';
import FabButton from '../common/FabButton';
import useAlert from '../../hooks/useAlert';
import PermissionGuard from '../routing/PermissionGuard';
import Pagination from '../common/Pagination';
import UnidadeRow from './UnidadeRow';
import UnidadeCard from './UnidadeCard';
import PageHeader from '../layout/PageHeader';
import ActionsDrawer from '../common/ActionsDrawer';

// ✅ Padronização das funções de extração (como no Agendamentos.js)
const extrairIdReal = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (obj.$oid) return String(obj.$oid);
    if (obj._id) {
        if (typeof obj._id === 'string') return obj._id;
        if (obj._id.$oid) return String(obj._id.$oid);
        return String(obj._id);
    }
    if (obj.id) {
        if (typeof obj.id === 'string') return obj.id;
        if (obj.id.$oid) return String(obj.id.$oid);
        return String(obj.id);
    }
    return String(obj);
};

const getArrayData = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.data && data.data.data && Array.isArray(data.data.data)) return data.data.data;
    return [];
};

const UnidadesPage = () => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const navigate = useNavigate();
    
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState('table'); 
    
    // Filtros locais separados dos ativos para permitir o submit
    const initialFilters = { search: '', status: '', localidade: '' };
    const [localFilters, setLocalFilters] = useState(initialFilters);
    const [activeFilters, setActiveFilters] = useState(initialFilters);
    const [selectedUnidade, setSelectedUnidade] = useState(null);

    const unidadesPerPage = 12;

    const contaId = useMemo(() => {
        if (!user) return null;
        return extrairIdReal(user.account);
    }, [user]);

    const { 
        data: unidadesData, 
        isLoading, 
        refetch: refetchUnidades 
    } = useUnidadesPorConta(contaId, {
        enabled: !!contaId
    });

    const { alternarStatusUnidade, deleteUnidade } = useMutateUnidades();

    const localidades = useMemo(() => {
        const locs = new Set();
        const lista = getArrayData(unidadesData);
        lista.forEach(u => {
            if (u.endereco && u.endereco.localidade) {
                locs.add(u.endereco.localidade);
            }
        });
        return Array.from(locs).sort();
    }, [unidadesData]);

    const unidades = useMemo(() => {
        let lista = getArrayData(unidadesData);
        
        return lista
            .filter(unidade => {
                if (activeFilters.search) {
                    const nome = unidade.nome?.toLowerCase() || '';
                    const loc = unidade.endereco?.localidade?.toLowerCase() || '';
                    const term = activeFilters.search.toLowerCase();
                    if (!nome.includes(term) && !loc.includes(term)) {
                        return false;
                    }
                }
                
                if (activeFilters.status) {
                    const isAtivo = unidade.ativo !== false;
                    if (activeFilters.status === 'ativo' && !isAtivo) return false;
                    if (activeFilters.status === 'inativo' && isAtivo) return false;
                }
                
                if (activeFilters.localidade && unidade.endereco?.localidade !== activeFilters.localidade) {
                    return false;
                }
                
                return true;
            })
            .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
    }, [unidadesData, activeFilters]);

    const totalUnidades = unidades.length;
    const startIndex = (currentPage - 1) * unidadesPerPage;
    const endIndex = startIndex + unidadesPerPage;
    const paginatedUnidades = unidades.slice(startIndex, endIndex);

    const handleNovaUnidade = useCallback(() => {
        navigate('/minha-conta/unidades/nova', { state: { contaId } });
    }, [navigate, contaId]);

    const handleGerenciarUnidade = useCallback((unidadeId) => {
        if (!unidadeId) return setAlert('ID da unidade inválido', 'danger');
        const idStr = String(unidadeId).trim();
        if (idStr === 'undefined' || idStr === 'null' || idStr === '') return setAlert('ID da unidade inválido', 'danger');
        
        navigate(`/minha-conta/unidades/${idStr}/gerenciar`, { state: { contaId } });
        setSelectedUnidade(null);
    }, [navigate, contaId, setAlert]);

    const handleToggleStatus = useCallback(async (unidade) => {
        const unidadeId = extrairIdReal(unidade);
        if (!unidadeId) return setAlert('Unidade inválida', 'danger');

        const action = unidade.ativo !== false ? 'desativar' : 'ativar';
        if (window.confirm(`Deseja ${action} a unidade "${unidade.nome}"?`)) {
            try {
                await alternarStatusUnidade.mutateAsync(unidadeId);
                setAlert(`Unidade ${action === 'ativar' ? 'ativada' : 'desativada'} com sucesso!`, 'success');
                refetchUnidades();
            } catch (error) {
                setAlert(error.message || `Erro ao ${action} unidade`, 'danger');
            }
        }
        setSelectedUnidade(null);
    }, [alternarStatusUnidade, setAlert, refetchUnidades]);

    const handleExcluirUnidade = useCallback(async (unidade) => {
        const unidadeId = extrairIdReal(unidade);
        if (!unidadeId) return setAlert('Unidade inválida', 'danger');

        if (window.confirm(`Tem certeza que deseja excluir a unidade "${unidade.nome}"? Esta ação não pode ser desfeita.`)) {
            try {
                await deleteUnidade.mutateAsync(unidadeId);
                setAlert('Unidade excluída com sucesso!', 'success');
                refetchUnidades();
            } catch (error) {
                setAlert(error.message || 'Erro ao excluir unidade', 'danger');
            }
        }
        setSelectedUnidade(null);
    }, [deleteUnidade, setAlert, refetchUnidades]);

    const handleFilterChange = (e) => {
        setLocalFilters({ ...localFilters, [e.target.name]: e.target.value });
    };

    const handleApplyFilters = useCallback((e) => { 
        if (e) e.preventDefault();
        setActiveFilters(localFilters);
        setCurrentPage(1); 
    }, [localFilters]);

    const handleClearFilters = useCallback(() => {
        setLocalFilters(initialFilters);
        setActiveFilters(initialFilters);
        setCurrentPage(1);
    }, [initialFilters]);

    const hasFilters = Object.values(localFilters).some(v => v !== '');

    if (isLoading) {
        return (
            <div className="page-management-container">
                <div className="flex justify-center items-center py-12">
                    <Loader2 size={32} className="animate-spin text-blue-600" />
                </div>
            </div>
        );
    }

    return (
        <PermissionGuard requiredPermission="unidades:manage">
            <div className="page-management-container">
                <PageHeader
                    icon={<MapPin />}
                    title="Unidades"
                    actions={<>
                        <div className="view-switcher">
                            <button onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'is-active' : ''} title="Cards">
                                <Grid3x3 size={16}/>
                            </button>
                            <button onClick={() => setViewMode('table')} className={viewMode === 'table' ? 'is-active' : ''} title="Tabela">
                                <List size={16}/>
                            </button>
                        </div>
                        <button onClick={() => setShowFilters(!showFilters)} className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}>
                            <Filter size={18} className="icon"/>
                            {showFilters ? 'Ocultar' : 'Exibir'} Filtros
                        </button>
                    </>}
                />

                {showFilters && (
                    <div className="content-panel mb-4 p-0 bg-transparent border-0 shadow-none">
                        <form onSubmit={handleApplyFilters} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                                
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-search" className="text-xs font-semibold text-gray-600 uppercase">Buscar Unidade</label>
                                    <div className="relative">
                                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input 
                                            type="text" 
                                            id="filter-search"
                                            name="search"
                                            value={localFilters.search} 
                                            onChange={handleFilterChange} 
                                            placeholder="Nome ou localidade..." 
                                            className="form-input text-sm py-2 px-3 pl-10 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all w-full"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-localidade" className="text-xs font-semibold text-gray-600 uppercase">Localidade</label>
                                    <select 
                                        id="filter-localidade"
                                        name="localidade"
                                        value={localFilters.localidade} 
                                        onChange={handleFilterChange} 
                                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white w-full"
                                    >
                                        <option value="">Todas</option>
                                        {localidades.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                                    </select>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-status" className="text-xs font-semibold text-gray-600 uppercase">Status</label>
                                    <select 
                                        id="filter-status"
                                        name="status"
                                        value={localFilters.status} 
                                        onChange={handleFilterChange} 
                                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white w-full"
                                    >
                                        <option value="">Todos</option>
                                        <option value="ativo">Ativas</option>
                                        <option value="inativo">Inativas</option>
                                    </select>
                                </div>

                                {/* Antes reinventava cor do zero em Tailwind (bg-blue-600)
                                    e o texto "Filtrar" so aparecia acima de 1280px */}
                                <div className="flex gap-2 h-full">
                                    <button
                                        type="submit"
                                        className="action-button action-button-primary flex-1 justify-center"
                                        title="Aplicar Filtros"
                                    >
                                        <Search size={18} className="icon" />
                                        Aplicar Filtros
                                    </button>

                                    {hasFilters && (
                                        <button
                                            type="button"
                                            onClick={handleClearFilters}
                                            className="action-button action-button-secondary justify-center"
                                            title="Limpar Filtros"
                                        >
                                            <X size={18} className="icon" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                        {paginatedUnidades.map((unidade, index) => {
                            const key = extrairIdReal(unidade) || `unidade-${index}`;
                            return (
                                <UnidadeCard
                                    key={key}
                                    unidade={unidade}
                                    onOpenActions={setSelectedUnidade}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="table-container">
                        <div className="table-wrapper">
                            <table className="styled-table w-full">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40%' }}>Nome da Unidade</th>
                                        <th style={{ width: '30%' }}>Localidade / UF</th>
                                        <th className="text-center" style={{ width: '15%' }}>Status</th>
                                        <th className="text-right pr-4" style={{ width: '15%' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedUnidades.map((unidade, index) => {
                                        const key = extrairIdReal(unidade) || `unidade-${index}`;
                                        return (
                                            <UnidadeRow
                                                key={key}
                                                unidade={unidade}
                                                onOpenActions={setSelectedUnidade}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {totalUnidades === 0 && (
                    <div className="table-empty-state py-12 mt-6 border rounded-lg">
                        <div className="bg-gray-100 p-4 rounded-full mb-4 inline-flex">
                            <MapPin className="text-gray-400" size={40} />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                            Nenhuma unidade encontrada
                        </h3>
                        <p className="text-gray-500 max-w-sm mx-auto mb-4">
                            {hasFilters
                                ? 'Tente ajustar os filtros de busca'
                                : 'Comece adicionando uma nova unidade'}
                        </p>
                        {!hasFilters && (
                            <button
                                onClick={handleNovaUnidade}
                                className="action-button action-button-primary mx-auto"
                            >
                                <Plus size={18} className="icon" />
                                Adicionar Unidade
                            </button>
                        )}
                    </div>
                )}

                {totalUnidades > unidadesPerPage && (
                    <div className="mt-6">
                        <Pagination
                            itemsPerPage={unidadesPerPage}
                            totalItems={totalUnidades}
                            onPageChange={setCurrentPage}
                            currentPage={currentPage}
                        />
                    </div>
                )}
            </div>

            <FabButton onClick={handleNovaUnidade} title="Adicionar Unidade" icon={<MapPin size={26} className="icon"/>} />

            <ActionsDrawer
                open={!!selectedUnidade}
                onClose={() => setSelectedUnidade(null)}
                title={selectedUnidade?.nome || 'Unidade'}
                actions={selectedUnidade ? [
                    {
                        key: 'editar',
                        label: 'Visualizar / Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleGerenciarUnidade(extrairIdReal(selectedUnidade)),
                    },
                    {
                        key: 'toggle-status',
                        label: selectedUnidade.ativo !== false ? 'Desativar' : 'Ativar',
                        icon: <Power size={20} className="icon" />,
                        onClick: () => handleToggleStatus(selectedUnidade),
                        loading: alternarStatusUnidade.isPending,
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleExcluirUnidade(selectedUnidade),
                        destructive: true,
                        loading: deleteUnidade.isPending,
                    },
                ] : []}
            />
        </PermissionGuard>
    );
};

export default UnidadesPage;